import { Component, effect, inject, input, output, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { map, switchMap } from 'rxjs';
import { AanwezigheidMeasure, CompoundFilterExpression, DataService, ExportFilter, FilterExpression } from '../../../services/data.service';
import { Attributes } from '../../../shared/dashboard/base-dashboard/base-dashboard-config';
import { AggregatorConfig } from '../../../shared/dashboard/base-dashboard/aggregator-config';
import { MultiAggregator, SingleAggregator, sumOver } from '../../../services/aggregation';
import { createAggFunctions } from '../../../shared/dashboard/base-dashboard/base-dashboard.component';
import { Level, unnest } from '../../../services/data-tree';
import { FilterService } from '../../../services/filter.service';
import {
	formatDurationUrenMinuten,
	getMaandLabelKort,
	getMaandLabelLangVoorlopig,
	getMaandNrFromLabelKort,
	getWeekdagLabelKort,
	getWeekdagLabelLang,
	getWeekdagNrFromLabelKort,
	isHuidigeMaand,
} from '@cumlaude/shared-utils';
import { IconComponent } from '@cumlaude/shared-components-icons';
import { TooltipDirective } from '@cumlaude/shared-components-overlays';
import { AanwezigheidMatrixComponent, MatrixCell, MatrixData } from '../aanwezigheid-matrix/aanwezigheid-matrix.component';
import { interventielaagForPerc } from '../../../services/schoolaanwezigheid';
import { ExportablePanel, TableExportPage } from '../../../services/exportable';
import { ExportOptions } from '../../../services/export.service';
import { ExportColumnDef } from '../../../shared/dashboard/data-tree-table/data-tree-table';
import { getFormattedValue } from '../../../shared/components/table/table/table.model';
import { formatDecimal } from '@cumlaude/shared-pipes';

export interface PerSchooldagI extends Attributes {
	aw_m_nr_lesuren: number;
	aw_m_nr_lesuren_aanwezig: number;
	aw_m_nr_minuten: number;
	aw_m_nr_minuten_aanwezig: number;
}

export type PerSchooldagA = PerSchooldagI;

@Component({
	selector: 'app-aanwezigheid-per-schooldag',
	templateUrl: './aanwezigheid-per-schooldag.component.html',
	styleUrls: ['./aanwezigheid-per-schooldag.component.scss'],
	imports: [IconComponent, TooltipDirective, AanwezigheidMatrixComponent],
})
export class AanwezigheidPerSchooldagComponent implements AggregatorConfig<PerSchooldagI, PerSchooldagA>, ExportablePanel {
	private readonly dataService = inject(DataService);
	private readonly filterService = inject(FilterService);

	readonly filterExpressions = input.required<FilterExpression[]>();
	readonly permanentFilters = input.required<FilterExpression[]>();

	private readonly filterExpressions$ = toObservable(this.filterExpressions);

	readonly schooljaar = toSignal(this.filterService.observeAsInput('aw_nm_schooljaar'));

	readonly loading = signal(false);
	loadingChanged = output<boolean>();

	constructor() {
		effect(() => {
			this.loadingChanged.emit(this.loading());
		});
	}

	getSingleAggregators(): Partial<{ [ai in keyof PerSchooldagA]: SingleAggregator<PerSchooldagI, PerSchooldagA[ai]> }> {
		return {
			aw_m_nr_lesuren: sumOver('aw_m_nr_lesuren'),
			aw_m_nr_lesuren_aanwezig: sumOver('aw_m_nr_lesuren_aanwezig'),
			aw_m_nr_minuten: sumOver('aw_m_nr_minuten'),
			aw_m_nr_minuten_aanwezig: sumOver('aw_m_nr_minuten_aanwezig'),
		};
	}

	getMultiAggregators(): MultiAggregator<keyof PerSchooldagA, PerSchooldagI, PerSchooldagA, PerSchooldagA[keyof PerSchooldagA]>[] {
		return [];
	}

	protected readonly perSchooldagData = toSignal(
		this.filterExpressions$.pipe(
			switchMap((filters) => {
				this.loading.set(true);
				return this.dataService.getAanwezigheidData({
					g: [['aw_d_datum', 'per_nr_weekdag'], ['aw_nr_maand']],
					f: new CompoundFilterExpression([...filters, ...this.permanentFilters()]),
					r: [0],
					m: [AanwezigheidMeasure.LESUREN, AanwezigheidMeasure.LESTIJD],
				});
			}),
			map((resp) => {
				const { aggInit, aggCombine } = createAggFunctions(this, [], resp.measures);
				const records = unnest<PerSchooldagA, number[]>(resp.data, undefined, 1, aggInit, aggCombine);
				this.loading.set(false);
				return records[0]?.[0];
			}),
			map((dataRoot: Level<PerSchooldagA, number[]>) => {
				if (!dataRoot?.c) {
					return undefined;
				}

				// Zorg dat we altijd alle 12 maanden tonen van aug t/m jul, ook als er geen data voor is
				const maanden = Array.from({ length: 12 }, (_, i) => (((i + 7) % 12) + 1).toString());
				const weekdagen = ['1', '2', '3', '4', '5']; // Ma t/m Vr

				// Maak MatrixData object
				const matrixData: MatrixData = {
					columnHeaders: maanden.map(getMaandLabelKort),
					columnClasses: maanden.map(() => 'onbekend'), // wordt nog gemuteerd
					rowHeaders: weekdagen.map(getWeekdagLabelKort),
					cells: [],
				};

				// Voor elke weekdag (rij)
				for (const weekdag of weekdagen) {
					const weekdagData = dataRoot.c?.find((wd) => wd.k === weekdag);
					const rijCellen: MatrixCell[] = [];

					// Voor elke maand (kolom)
					for (let i = 0; i < maanden.length; i++) {
						const maand = maanden[i];
						const maandData = weekdagData?.c?.find((m) => m.k === maand);
						if (maandData?.a && maandData.a.aw_m_nr_minuten > 0) {
							const frac = maandData.a.aw_m_nr_minuten_aanwezig / maandData.a.aw_m_nr_minuten;
							const nr_lesuren = maandData.a.aw_m_nr_lesuren ?? 0;
							const nr_lesuren_aanwezig = maandData.a.aw_m_nr_lesuren_aanwezig ?? 0;
							const perc = frac * 100;
							const voorlopig = isHuidigeMaand(this.schooljaar(), maand);
							const maandLabel = getMaandLabelLangVoorlopig(this.schooljaar(), maand);
							const tooltipHeader = `${getWeekdagLabelLang(weekdag)}en in ${maandLabel.toLowerCase()}`;

							const tooltip = [
								{ label: '', value: tooltipHeader, bold: true },
								`${getFormattedValue(frac, 'percentage', '1.2-2')} (${interventielaagForPerc(frac)})`,
								`${formatDecimal(nr_lesuren_aanwezig)} / ${formatDecimal(nr_lesuren)} lesuren`,
								`${formatDurationUrenMinuten(maandData.a.aw_m_nr_minuten_aanwezig, 'hu mm')} aanwezig`,
								`${formatDurationUrenMinuten(maandData.a.aw_m_nr_minuten - maandData.a.aw_m_nr_minuten_aanwezig, 'hu mm')} gemist`,
							];

							rijCellen.push({
								value: perc,
								displayValue: `${getFormattedValue(frac, 'percentage', '1.0-0')}`,
								tooltip,
								voorlopig,
							});
							matrixData.columnClasses[i] = voorlopig ? 'voorlopig' : ''; // 1 gevulde weekdag -> hele maand niet meer 'onbekend'
						} else {
							// Geen data voor deze maand/weekdag combinatie
							rijCellen.push({ disabled: true, tooltip: 'Geen data beschikbaar' });
						}
					}

					matrixData.cells.push(rijCellen);
				}

				return matrixData;
			})
		)
	);

	async exportAsTable(exportOptions: ExportOptions, filters: ExportFilter[]): Promise<TableExportPage> {
		const perSchooldagData = this.perSchooldagData()!;

		return {
			name: 'Aanwezigheid per schooldag',
			...(exportOptions.showTitle ? { title: 'Gemiddelde aanwezigheid per schooldag' } : {}),
			...(exportOptions.showFilters ? { filters } : {}),
			data: [
				{
					columns: [
						{ name: '', type: 'string' },
						...perSchooldagData.columnHeaders.map(
							(header) =>
								<ExportColumnDef>{
									name: getMaandLabelLangVoorlopig(this.schooljaar(), getMaandNrFromLabelKort(header)),
									type: 'percentage',
									format: '1.0-0',
								}
						),
					],
					rows: perSchooldagData.rowHeaders.map((rowHeader, rowIndex) => [
						getWeekdagLabelLang(getWeekdagNrFromLabelKort(rowHeader)),
						...perSchooldagData.cells[rowIndex].map((cell) => (cell.disabled ? null : cell.value! / 100)),
					]),
				},
			],
		};
	}
}
